import { UniqueIdentifier } from "@dnd-kit/core";
import { SortableTree } from "@redotech/arbiter/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/arbiter/navigation/folder-tree/types";
import { flattenTree } from "@redotech/arbiter/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/arbiter/navigation/secondary/redo-secondary-nav-item";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { useCanEditCatalogViews } from "@redotech/redo-merchant-app-common/catalog/catalog-access";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { CatalogProductsViewsContext } from "@redotech/redo-merchant-inventory-app/inventory-views";
import { memo, useMemo } from "react";
import {
  FolderWithView,
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "../orders/view-to-tree-utils";

export const CatalogViews = memo(function CatalogViews() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { views, folders, openEditFolderModal, updateOrdering } =
    useRequiredContext(CatalogProductsViewsContext);
  const urlPrefix = `${routes.catalogBaseUrl}/products`;
  const canEditViews = useCanEditCatalogViews();

  const viewsWithoutFolders = useMemo(() => {
    return views?.filter((view) => !view.folderId) || [];
  }, [views]);

  const foldersWithViews = useMemo<FolderWithView<"catalog-products">[]>(() => {
    return (
      folders?.map((folder) => {
        return {
          folder,
          views: views?.filter((view) => view.folderId === folder._id) || [],
        };
      }) || []
    );
  }, [folders, views]);

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: {},
      folderCounts: {},
      hideZeroNotifications: false,
    });
  }, [viewsWithoutFolders, foldersWithViews, urlPrefix]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const result = treeToViewsAndFolders<"catalog-products">(
      flattenedItems,
      views || [],
      folders || [],
    );
    updateOrdering(result.views, result.folders);
  });

  return (
    <>
      <SortableTree
        disableDrag={!canEditViews}
        items={sortableItems}
        onRequestEdit={canEditViews ? handleRequestEdit : undefined}
        setItems={handleUpdateOrdering}
      />
      {canEditViews && <CatalogViewsCreateFolderItem />}
    </>
  );
});

const CatalogViewsCreateFolderItem = memo(
  function CatalogViewsCreateFolderItem() {
    const { t } = useTranslation();
    const { openCreateFolderModal } = useRequiredContext(
      CatalogProductsViewsContext,
    );

    return (
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => {
            openCreateFolderModal();
          },
        }}
        friendlyName={t("+ Add new folder", {
          context: "inventory and purchasing navigation label",
        })}
        id="catalog-products-create-folder"
      />
    );
  },
);
